import { useState } from 'react';
import { SEO } from '@/components/SEO';
import { PublicHeader } from '@/components/marketing/PublicHeader';
import { PublicFooter } from '@/components/marketing/PublicFooter';
import { InsightCard } from '@/components/insights/InsightCard';
import { CategoryFilter } from '@/components/insights/CategoryFilter';
import { InsightsGridSkeleton } from '@/components/insights/InsightsSkeleton';
import { useInsightPosts, useInsightCategories } from '@/hooks/useInsights';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowRight, Lightbulb } from 'lucide-react';

export default function Insights() {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  
  const { data: posts, isLoading: postsLoading } = useInsightPosts(selectedCategory || undefined);
  const { data: categories, isLoading: categoriesLoading } = useInsightCategories();

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'Radius Insights',
    description: 'Expert insights on procurement, vendor management, and B2B sourcing best practices from the Radius team.',
    url: 'https://useradius.io/insights',
    publisher: {
      '@type': 'Organization',
      name: 'Radius',
      logo: {
        '@type': 'ImageObject',
        url: 'https://useradius.io/logo.png',
      },
    },
  };

  return (
    <>
      <SEO
        title="Insights | Radius"
        description="Expert insights on procurement, vendor management, and B2B sourcing best practices from the Radius team."
        canonical="/insights"
        type="website"
        structuredData={structuredData}
      />
      
      <div className="min-h-screen flex flex-col bg-background">
        <PublicHeader />
        
        <main className="flex-1">
          {/* Hero Section */}
          <section className="relative overflow-hidden py-16 md:py-24 border-b bg-gradient-to-b from-primary/5 via-background to-background">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,hsl(var(--primary)/0.1),transparent_50%)]" />
            <div className="container relative">
              <div className="max-w-3xl mx-auto text-center space-y-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium">
                  <Lightbulb className="h-4 w-4" />
                  Expert Perspectives
                </div>
                <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground">
                  Insights
                </h1>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Expert insights on procurement, vendor management, and B2B sourcing best practices. 
                  Stay ahead with strategies that drive results.
                </p>
              </div>
            </div>
          </section>
          
          {/* Content Section */}
          <section className="py-12 md:py-16">
            <div className="container">
              {/* Category Filter */}
              {!categoriesLoading && categories && categories.length > 0 && (
                <div className="mb-8">
                  <CategoryFilter
                    categories={categories}
                    selectedCategory={selectedCategory}
                    onSelectCategory={setSelectedCategory}
                  />
                </div>
              )}
              
              {/* Posts Grid */}
              {postsLoading ? (
                <InsightsGridSkeleton />
              ) : posts && posts.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {posts.map((post) => (
                    <InsightCard key={post._id} post={post} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <p className="text-muted-foreground">No posts found.</p>
                </div>
              )}
            </div>
          </section>
          
          {/* CTA Section */}
          <section className="py-16 md:py-24 bg-primary/5 border-t">
            <div className="container">
              <div className="max-w-3xl mx-auto text-center space-y-6">
                <h2 className="text-3xl font-bold text-foreground">
                  Ready to Transform Your Procurement?
                </h2>
                <p className="text-lg text-muted-foreground">
                  Join thousands of businesses using Radius to streamline vendor management 
                  and make smarter sourcing decisions.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button size="lg" asChild>
                    <Link to="/auth/signup">
                      Get Started Free
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                  <Button size="lg" variant="outline" asChild>
                    <Link to="/contact">Contact Sales</Link>
                  </Button>
                </div>
              </div>
            </div>
          </section>
        </main>
        
        <PublicFooter />
      </div>
    </>
  );
}
